import {
  takeLatest,
  call,
  put,
  take,
  race,
  cancel,
  select,
} from 'redux-saga/effects';
import get from 'lodash/get';

import { TODO_TYPES } from '../../../constants';
import { INSURANCE_ELIGIBILITY_FIELDS_TYPES } from '../../../constants/formFields';

import {
  MANAGE_INSURANCES_ACTION_TYPES,
  manageInsurancesActions,
} from '../actions/manage-insurances-actions';

import {
  actions as completeTaskActions,
  consts as completeTaskConsts,
} from '../../healthboard/requests/complete-task-req';
import { insuranceErrorModalActions } from '../../appointmentForm/handle-insurance-error-modal';
import {
  actions as createPatientInsuranceActions,
  consts as createPatientInsuranceConsts,
} from '../../manage-insurances/requests/create-patient-insurance';
import {
  actions as updatePatientInsuranceActions,
  consts as updatePatientInsuranceConsts,
} from '../../manage-insurances/requests/update-patient-insurance';

import { getRequestPayloadFromFormState } from '../../../utils/manage-insurances/get-request-payload-from-form-state';
import { convertInsuranceFormDataToFormData } from '../../../utils/manage-insurances/convert-insurance-form-data-to-form-data';

import { selectIsAdditionalInsurancesEnabled } from '../selectors/get-patient-insurances-list';

const { DO_NOT_HAVE_INSURANCE } = INSURANCE_ELIGIBILITY_FIELDS_TYPES;

const UPDATE_PATIENT_INSURANCE = {
  SUCCEED: [updatePatientInsuranceConsts.SUCCEED],
  FAIL: [
    updatePatientInsuranceConsts.REJECTED,
    updatePatientInsuranceConsts.ERROR_ENCOUNTERED,
  ],
};

const CREATE_PATIENT_INSURANCE = {
  SUCCEED: [createPatientInsuranceConsts.SUCCEED],
  FAIL: [
    createPatientInsuranceConsts.REJECTED,
    createPatientInsuranceConsts.ERROR_ENCOUNTERED,
  ],
};

function* sendAndAwait({ sendAction, succeedTypes, failTypes }) {
  yield put(sendAction);

  const { succeed, failed } = yield race({
    succeed: take(succeedTypes),
    failed: take(failTypes),
  });

  return { succeed, failed };
}

function* buildMainInsurancePayload({
  mainInsuranceFormState,
  userId,
  taskAssignmentId,
  saveAnyway,
}) {
  const mainInsuranceFormData = yield call(
    getRequestPayloadFromFormState,
    mainInsuranceFormState,
  );
  const skipInsurance = Boolean(mainInsuranceFormState[DO_NOT_HAVE_INSURANCE]);

  const insuranceId = get(mainInsuranceFormState, 'id');

  return {
    data: {
      ...mainInsuranceFormData,
      userId,
      skipInsurance,
      context: TODO_TYPES.TASK,
      todoTaskAssignmentId: taskAssignmentId,
      skipEligibilityCheck: Boolean(saveAnyway),
      patientInsuranceId: insuranceId,
      status: 'completed',
    },
    insuranceId,
    skipInsurance,
  };
}

function* buildAdditionalInsurancePayload({
  insuranceFormState,
  userId,
  taskAssignmentId,
}) {
  const insuranceFormData = yield call(
    getRequestPayloadFromFormState,
    insuranceFormState,
  );
  const insuranceId = get(insuranceFormState, 'id');

  const formData = yield call(convertInsuranceFormDataToFormData, {
    insuranceFormData,
    userId,
    insuranceId,
  });

  formData['context'] = TODO_TYPES.TASK;
  formData['todo_task_assignment_id'] = taskAssignmentId;

  return { payload: formData, insuranceId };
}

function* upsertInsurance({ hasId, payload }) {
  if (hasId) {
    return yield call(sendAndAwait, {
      sendAction: updatePatientInsuranceActions.send(payload),
      succeedTypes: UPDATE_PATIENT_INSURANCE.SUCCEED,
      failTypes: UPDATE_PATIENT_INSURANCE.FAIL,
    });
  }

  return yield call(sendAndAwait, {
    sendAction: createPatientInsuranceActions.send(payload),
    succeedTypes: CREATE_PATIENT_INSURANCE.SUCCEED,
    failTypes: CREATE_PATIENT_INSURANCE.FAIL,
  });
}

function* handleAdditionalInsuranceAction({
  insuranceFormState,
  userId,
  todoTaskAssignmentId,
}) {
  const { payload, insuranceId: addId } = yield call(
    buildAdditionalInsurancePayload,
    {
      insuranceFormState,
      userId,
      taskAssignmentId: todoTaskAssignmentId,
    },
  );

  yield call(upsertInsurance, { hasId: Boolean(addId), payload });
}

export function* saveToDoInsurancesDataWorker({ payload }) {
  yield put(manageInsurancesActions.setInsuranceToDoTaskCompleting(true));

  const isAdditionalInsurancesEnabled = yield select(
    selectIsAdditionalInsurancesEnabled,
  );

  try {
    const {
      userId,
      mainInsuranceFormState,
      additionalInsurancesFormState = [],
      finishAction,
      taskAssignmentId,
      saveAnyway = false,
    } = payload;

    if (saveAnyway) {
      yield put(insuranceErrorModalActions.close());
    }

    if (additionalInsurancesFormState.length && isAdditionalInsurancesEnabled) {
      for (const insuranceFormState of additionalInsurancesFormState) {
        yield call(handleAdditionalInsuranceAction, {
          insuranceFormState,
          userId,
          todoTaskAssignmentId: taskAssignmentId,
        });
      }
    }

    const { data, insuranceId } = yield call(buildMainInsurancePayload, {
      mainInsuranceFormState,
      userId,
      taskAssignmentId,
      saveAnyway,
    });

    const { failed } = yield call(upsertInsurance, {
      hasId: Boolean(insuranceId),
      payload: data,
    });

    if (failed) {
      yield cancel();
      return;
    }

    yield put(
      completeTaskActions.start({
        status: 'completed',
        id: taskAssignmentId,
        userId,
      }),
    );

    yield take(completeTaskConsts.SUCCEED);

    if (finishAction) {
      yield call(finishAction);
    }
  } finally {
    yield put(manageInsurancesActions.setInsuranceToDoTaskCompleting(false));
  }
}

export function* saveToDoInsurancesData() {
  yield takeLatest(
    MANAGE_INSURANCES_ACTION_TYPES.SAVE_TO_DO_INSURANCES_DATA,
    saveToDoInsurancesDataWorker,
  );
}



// WEBPACK FOOTER //
// ./src/redux/manage-insurances/sagas/save-to-do-insurances-data.js